/**
 * Catálogo oficial de destinos de la app (correo / push / puente web).
 * Base pública: https://www.aipeces.com{path}
 */

export type AppDeepLink = {
  id: string;
  label: string;
  /** Ruta canónica a insertar en click_url */
  path: string;
  /** Parámetros habituales (documentación) */
  params?: string;
  notes?: string;
};

export const APP_DEEP_LINKS: readonly AppDeepLink[] = [
  {
    id: 'add-catch',
    label: 'Añadir captura',
    path: '/add-catch',
  },
  {
    id: 'profile-onboarding',
    label: 'Perfil · completar (onboarding)',
    path: '/profile?onboarding=1',
    params: 'step=username|avatar|water_type|fishing_modes|location_permission|notification',
  },
  {
    id: 'rewards',
    label: 'Recompensas / Tesoros',
    path: '/profile?tab=nivel&premios=1',
  },
  {
    id: 'missions',
    label: 'Misiones',
    path: '/profile?tab=nivel',
  },
  {
    id: 'ambassador',
    label: 'Misión Embajador',
    path: '/profile?tab=nivel&mission=invite_friend',
    notes: 'Scroll + highlight de la misión élite Embajador.',
  },
  {
    id: 'pro-paywall',
    label: 'Pro / muro de pago',
    path: '/profile?openSubscription=1&interval=annual',
    notes: 'Abre SubscriptionModal con plan anual preseleccionado.',
  },
  {
    id: 'stats',
    label: 'Estadísticas',
    path: '/profile?tab=estadisticas',
  },
  {
    id: 'patterns',
    label: 'Análisis de zona',
    path: '/patterns',
  },
  {
    id: 'diary',
    label: 'Diario (Pescadillo)',
    path: '/intel/pescadillo',
    notes: 'Sin mode=diary en URL todavía.',
  },
  {
    id: 'diary-captures',
    label: 'Diario · capturas (perfil)',
    path: '/profile?tab=capturas',
    notes: 'Lista cronológica de capturas del usuario (pestaña Actividad).',
  },
  {
    id: 'wallet',
    label: 'Monedero',
    path: '/tienda/cartera',
  },
  {
    id: 'planificar',
    label: 'Planificar salida',
    path: '/rincon?tab=planificar',
  },
  {
    id: 'store',
    label: 'Tienda',
    path: '/tienda',
  },
  {
    id: 'clubs',
    label: 'Clubes (Rincón)',
    path: '/rincon',
    params: 'club=|clubSlug=|clubInvite=|clubJoin=',
  },
  {
    id: 'get-app',
    label: 'Smart link / get-app',
    path: '/get-app',
  },
  {
    id: 'board',
    label: 'Comunidad (feed)',
    path: '/board',
  },
] as const;

const KNOWN_PATH_PREFIXES = [
  '/add-catch',
  '/profile',
  '/patterns',
  '/intel/pescadillo',
  '/tienda',
  '/rincon',
  '/get-app',
  '/board',
  '/clubes',
  '/c/',
  '/m/',
  '/u/',
  '/s/',
  '/invite/',
  '/catch/',
  '/moment/',
  '/subscription/',
  '/onboarding',
  '/auth',
] as const;

function stripOrigin(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const u = new URL(trimmed);
      if (/(^|\.)aipeces\.com$/i.test(u.hostname) || u.hostname === 'localhost') {
        return `${u.pathname}${u.search}`;
      }
    }
  } catch {
    /* ignore */
  }
  return trimmed;
}

export function isExternalHttps(url: string): boolean {
  const t = url.trim();
  if (!/^https:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function isKnownAppDeepLink(url: string): boolean {
  const path = stripOrigin(url);
  if (!path) return true;
  if (path.startsWith('/')) {
    return KNOWN_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}/`)
    );
  }
  return false;
}

/** true si conviene mostrar aviso (ni ruta de app conocida ni https externa). */
export function shouldWarnDestination(url: string): boolean {
  const t = url.trim();
  if (!t) return false;
  if (isKnownAppDeepLink(t)) return false;
  if (isExternalHttps(t)) return false;
  return true;
}

export function matchDeepLinkId(url: string): string | '' {
  const path = stripOrigin(url);
  if (!path) return '';
  const exact = APP_DEEP_LINKS.find((d) => d.path === path);
  if (exact) return exact.id;
  if (path.startsWith('/profile?tab=nivel&premios=1')) return 'rewards';
  if (path.includes('mission=invite_friend')) return 'ambassador';
  if (path.startsWith('/profile?onboarding=1')) return 'profile-onboarding';
  if (path.includes('openSubscription=1')) return 'pro-paywall';
  if (path.startsWith('/profile?tab=estadisticas')) return 'stats';
  if (path.startsWith('/profile?tab=nivel')) return 'missions';
  if (path.startsWith('/rincon?tab=planificar')) return 'planificar';
  if (path === '/rincon' || path.startsWith('/rincon?')) return 'clubs';
  if (path.startsWith('/tienda/cartera')) return 'wallet';
  if (path === '/tienda' || path.startsWith('/tienda?') || path.startsWith('/tienda/')) return 'store';
  if (path.startsWith('/patterns')) return 'patterns';
  if (path.startsWith('/intel/pescadillo')) return 'diary';
  if (path.startsWith('/add-catch')) return 'add-catch';
  if (path.startsWith('/get-app')) return 'get-app';
  if (path.startsWith('/board')) return 'board';
  return '';
}
